'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Check, Copy, Download, Loader2, Search } from 'lucide-react';
import { Link } from '@/i18n/routing';
import type { AiReport, BotRow } from '@/lib/site-checker/check';
import { PRESETS, robotsSnippet } from '@/lib/site-checker/bots';
import type { Advice } from '@/lib/site-checker/score';
import {
  perevesti,
  podrobnost,
  sPovtorami,
  type OtchetSeo,
  type OstatokSeo,
  type ZamechanieSeo,
} from '@/lib/seo-analiz';

/**
 * Проверка сайта: одна форма, две вкладки.
 *
 * «ИИ-поиск» — наш маршрут /api/tools/ai-readiness. «Классическое SEO» —
 * тот же анализатор, что на aisearch.ru/seo-analiz, через
 * /api/seo/analyze. Оба запроса стартуют одновременно: человек ждёт один
 * раз, а вкладки заполняются по мере готовности.
 *
 * Замечания SEO-анализатора приходят по-английски. На английской
 * странице показываем как есть, на остальных языках тоже как есть с
 * пояснением уровня: переводить полсотни шаблонов на три языка ради
 * первой версии не стоит, а русский перевод живёт в витрине aisearch.ru.
 */

type Tab = 'ai' | 'seo';
type AiState =
  | { status: 'idle' | 'loading' }
  | { status: 'done'; report: AiReport; cached: boolean }
  | { status: 'error'; code: string };
type SeoState =
  | { status: 'idle' | 'loading' }
  | { status: 'done'; report: OtchetSeo; quota: OstatokSeo | null }
  | { status: 'limit' }
  | { status: 'error' };

const LEVEL_DOT: Record<string, string> = {
  critical: 'bg-red-500',
  error: 'bg-orange-500',
  important: 'bg-amber-400',
  warning: 'bg-amber-400',
  nice: 'bg-sky-400',
  info: 'bg-sky-400',
  good: 'bg-emerald-500',
};

function scoreColor(n: number): string {
  if (n >= 80) return 'text-emerald-500 border-emerald-500';
  if (n >= 50) return 'text-amber-500 border-amber-500';
  return 'text-red-500 border-red-500';
}

function ScoreRing({ value, label }: { value: number | null; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`flex h-20 w-20 items-center justify-center rounded-full border-4 text-2xl font-bold ${
          value === null
            ? 'text-[var(--color-text-muted)] border-[var(--color-border)]'
            : scoreColor(value)
        }`}
      >
        {value ?? '—'}
      </div>
      <span className="text-xs text-[var(--color-text-muted)] text-center">{label}</span>
    </div>
  );
}

function CopyBox({ text, filename }: { text: string; filename: string }) {
  const t = useTranslations('site_checker');
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)]">
      <div className="flex justify-end gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => {
                setCopied(false);
              }, 1500);
            });
          }}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm hover:bg-[var(--color-bg-elevated)]"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? t('copied') : t('copy')}
        </button>
        <button
          type="button"
          onClick={() => {
            const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm hover:bg-[var(--color-bg-elevated)]"
        >
          <Download className="h-4 w-4" />
          {t('download')}
        </button>
      </div>
      <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words p-4 text-sm">
        {text}
      </pre>
    </div>
  );
}

/**
 * Самое тяжёлое из SEO-отчёта для списка «что исправить сначала».
 *
 * Раньше список строился только из советов проверки для ИИ, и рядом с
 * «SEO 26» стояло «критичных проблем нет» (Seo-marketing, 10.10.2026).
 * Предупреждения сюда не идут: оценку они почти не роняют (seoaudit.Score
 * в API), а список нужен для того, что роняет.
 */
function glavnoeSeo(issues: ZamechanieSeo[] | null | undefined): ZamechanieSeo[] {
  const ves = (z: ZamechanieSeo) => (z.level === 'critical' ? 0 : 1);
  return (issues ?? [])
    .filter((z) => z.level === 'critical' || z.level === 'error')
    .sort((a, b) => ves(a) - ves(b))
    .slice(0, 3);
}

function AdviceList({
  items,
  seo = [],
  ru = false,
}: {
  items: Advice[];
  seo?: ZamechanieSeo[];
  ru?: boolean;
}) {
  const t = useTranslations('site_checker');
  if (items.length === 0 && seo.length === 0)
    return <p className="text-emerald-500">{t('all_good')}</p>;
  return (
    <ul className="space-y-2">
      {seo.map((z, i) => (
        <li
          key={`seo-${z.message}-${String(i)}`}
          className="flex gap-3 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
        >
          <span
            className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${LEVEL_DOT[z.level] ?? 'bg-gray-400'}`}
          />
          <span className="break-words">
            SEO: {sPovtorami((ru ? perevesti(z.message).nazvanie : null) ?? z.message, z)}
          </span>
        </li>
      ))}
      {items.map((a) => (
        <li
          key={a.key}
          className="flex gap-3 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
        >
          <span
            className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${LEVEL_DOT[a.level] ?? 'bg-gray-400'}`}
          />
          <span className="break-words">
            {t(`adv_${a.key}`, { items: (a.items ?? []).join(', ') })}
          </span>
        </li>
      ))}
    </ul>
  );
}

type T = ReturnType<typeof useTranslations<'site_checker'>>;

function liveLabel(b: BotRow, t: T): string {
  if (b.live === null) return t('live_none');
  if (b.challenged) return `${String(b.live)} · ${t('live_wall')}`;
  if (b.live === 0) return t('live_fail');
  return String(b.live);
}

/** Живой заход не дошёл до страницы: стена, отказ, ошибка сервера. */
function liveBad(b: BotRow): boolean {
  return b.live !== null && (b.challenged || b.live === 0 || b.live >= 400);
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <tr className="border-b border-[var(--color-border)] last:border-0 align-top">
      <th className="w-48 py-2 pr-3 text-left font-medium text-[var(--color-text-muted)]">{k}</th>
      <td className="py-2 break-all">{v}</td>
    </tr>
  );
}

function AiTab({ r }: { r: AiReport }) {
  const t = useTranslations('site_checker');
  const [preset, setPreset] = useState<keyof typeof PRESETS>('searchOnly');
  const snippet = useMemo(() => robotsSnippet(PRESETS[preset]), [preset]);
  const yn = (b: boolean) => (b ? t('yes') : t('no'));
  const robotsWord = (v: BotRow['robots']) =>
    v === 'allowed'
      ? t('robots_allowed')
      : v === 'partial'
        ? t('robots_partial')
        : t('robots_blocked');

  return (
    <div className="space-y-10">
      <section>
        <h3 className="mb-3 text-xl font-semibold">{t('bots_title')}</h3>
        {!r.robots.found && (
          <p className="mb-3 text-sm text-[var(--color-text-muted)]">{t('robots_missing')}</p>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-text-muted)]">
                <th className="py-2 pr-3">{t('col_bot')}</th>
                <th className="py-2 pr-3">{t('col_purpose')}</th>
                <th className="py-2 pr-3">{t('col_robots')}</th>
                <th className="py-2">{t('col_live')}</th>
              </tr>
            </thead>
            <tbody>
              {r.bots.map((b) => {
                return (
                  <tr key={b.token} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="py-2 pr-3">
                      <span className="font-medium">{b.token}</span>
                      <span className="block text-xs text-[var(--color-text-muted)]">
                        {b.owner}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-[var(--color-text-muted)]">
                      {t(`purpose_${b.purpose}`)}
                    </td>
                    <td
                      className={`py-2 pr-3 ${b.robots === 'blocked' ? 'text-red-500' : b.robots === 'partial' ? 'text-amber-500' : ''}`}
                    >
                      {robotsWord(b.robots)}
                    </td>
                    <td className={`py-2 ${liveBad(b) ? 'text-red-500' : ''}`}>
                      {liveLabel(b, t)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {r.walled && <p className="mt-3 text-sm text-amber-500">{t('walled_note')}</p>}
      </section>

      <section>
        <h3 className="mb-3 text-xl font-semibold">{t('llms_title')}</h3>
        {!r.llms.check ? (
          <p className="text-sm">{t('llms_missing')}</p>
        ) : r.llms.check.isHtml ? (
          <p className="text-sm text-red-500">{t('llms_html')}</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              <Row k={t('llms_row_title')} v={r.llms.check.h1 ?? t('no')} />
              <Row k={t('llms_summary')} v={yn(r.llms.check.hasSummary)} />
              <Row k={t('llms_sections')} v={String(r.llms.check.sections)} />
              <Row k={t('llms_links')} v={String(r.llms.check.links.length)} />
              <Row
                k={t('llms_broken')}
                v={r.llms.brokenLinks.length ? r.llms.brokenLinks.join('\n') : '0'}
              />
              <Row k={t('llms_full')} v={yn(r.llmsFull.ok)} />
            </tbody>
          </table>
        )}
      </section>

      {r.html && (
        <section>
          <h3 className="mb-3 text-xl font-semibold">{t('text_title')}</h3>
          <table className="w-full text-sm">
            <tbody>
              <Row k={t('words')} v={String(r.html.words)} />
              <Row k={t('page_title')} v={r.html.title ?? t('no')} />
              <Row k={t('page_desc')} v={r.html.description ?? t('no')} />
              <Row k={t('jsonld')} v={r.html.jsonLdTypes.join(', ') || t('no')} />
              <Row
                k={t('sitemap')}
                v={
                  r.sitemap.url
                    ? `${r.sitemap.url} · ${t('sitemap_urls', { n: r.sitemap.urls })}`
                    : t('no')
                }
              />
            </tbody>
          </table>
        </section>
      )}

      {!r.walled && (
        <section>
          <h3 className="mb-1 text-xl font-semibold">{t('draft_title')}</h3>
          <p className="mb-3 text-sm text-[var(--color-text-muted)]">{t('draft_hint')}</p>
          <CopyBox text={r.draftLlms} filename="llms.txt" />
        </section>
      )}

      <section>
        <h3 className="mb-1 text-xl font-semibold">{t('robots_gen_title')}</h3>
        <p className="mb-3 text-sm text-[var(--color-text-muted)]">{t('robots_gen_hint')}</p>
        <div className="mb-3 flex flex-wrap gap-2">
          {(
            [
              ['allowAll', 'preset_allow'],
              ['searchOnly', 'preset_search'],
              ['blockAll', 'preset_block'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setPreset(k);
              }}
              aria-pressed={preset === k}
              className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                preset === k
                  ? 'border-[var(--color-accent-500)] bg-[var(--color-accent-500)]/10'
                  : 'border-[var(--color-border)] hover:border-[var(--color-accent-500)]'
              }`}
            >
              {t(label)}
            </button>
          ))}
        </div>
        {snippet ? (
          <CopyBox text={snippet} filename="robots-ai.txt" />
        ) : (
          <p className="text-sm">{t('robots_gen_empty')}</p>
        )}
      </section>
    </div>
  );
}

const SEO_LEVELS = ['critical', 'error', 'warning', 'info', 'good'] as const;

function SeoTab({ state }: { state: SeoState }) {
  const t = useTranslations('site_checker');
  // Анализатор пишет замечания по-английски. Русский перевод есть тот же,
  // что на aisearch.ru/seo-analiz; для es и sr его пока нет.
  const ru = useLocale() === 'ru';
  if (state.status !== 'done' && state.status !== 'limit' && state.status !== 'error')
    return (
      <p className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('checking')}
      </p>
    );
  if (state.status === 'limit')
    return (
      <div className="rounded-xl border border-[var(--color-accent-500)]/40 p-5">
        <p className="mb-3 font-semibold">{t('seo_limit')}</p>
        <Link
          href="/register"
          className="inline-block rounded-xl bg-[var(--color-brand-500)] px-5 py-2.5 font-semibold text-[var(--color-on-brand)]"
        >
          {t('seo_signup')}
        </Link>
      </div>
    );
  if (state.status === 'error') return <p className="text-sm text-red-500">{t('seo_error')}</p>;

  const r = state.report;
  const issues = r.issues ?? [];
  return (
    <div className="space-y-6">
      {state.quota && (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('seo_quota', { left: state.quota.free_left, total: state.quota.free_per_day })}
        </p>
      )}
      {SEO_LEVELS.map((lvl) => {
        const list = issues.filter((z) => z.level === lvl);
        if (list.length === 0) return null;
        return (
          <div key={lvl}>
            <h3 className="mb-2 flex items-center gap-2 font-semibold">
              <span className={`h-2.5 w-2.5 rounded-full ${LEVEL_DOT[lvl] ?? 'bg-gray-400'}`} />
              {t(`lvl_${lvl}`)} ({list.length})
            </h3>
            <ul className="space-y-2">
              {list.map((z, i) => (
                <li
                  key={`${z.message}-${String(i)}`}
                  className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
                >
                  <SeoIssue z={z} ru={ru} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function SeoIssue({ z, ru }: { z: ZamechanieSeo; ru: boolean }) {
  const p = ru ? perevesti(z.message) : null;
  const details = ru ? podrobnost(z.details) : z.details;
  const sovet = ru ? p?.sovet : z.recommendation;
  return (
    <>
      <p className="font-medium">{sPovtorami(p?.nazvanie ?? z.message, z)}</p>
      {details && <p className="break-words text-[var(--color-text-muted)]">{details}</p>}
      {sovet && <p className="break-words">{sovet}</p>}
    </>
  );
}

export function SiteChecker({ defaultTab }: { defaultTab: Tab }) {
  const t = useTranslations('site_checker');
  const locale = useLocale();
  const [url, setUrl] = useState('');
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [ai, setAi] = useState<AiState>({ status: 'idle' });
  const [seo, setSeo] = useState<SeoState>({ status: 'idle' });
  const [started, setStarted] = useState(false);
  const live = useRef<AbortController | null>(null);

  const run = useCallback(async (value: string) => {
    const v = value.trim();
    if (v === '') return;
    live.current?.abort();
    const ctrl = new AbortController();
    live.current = ctrl;
    setStarted(true);
    setAi({ status: 'loading' });
    setSeo({ status: 'loading' });
    const u = new URL(window.location.href);
    u.searchParams.set('url', v);
    window.history.replaceState(null, '', u.toString());

    const aiJob = fetch('/api/tools/ai-readiness', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: v }),
      signal: ctrl.signal,
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as {
          report?: AiReport;
          cached?: boolean;
          error?: { code?: string };
        };
        if (ctrl.signal.aborted) return;
        if (res.ok && body.report)
          setAi({ status: 'done', report: body.report, cached: Boolean(body.cached) });
        else setAi({ status: 'error', code: body.error?.code ?? 'failed' });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setAi({ status: 'error', code: 'network' });
      });

    const seoUrl = /^https?:\/\//i.test(v) ? v : `https://${v}`;
    const seoJob = fetch('/api/seo/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: seoUrl }),
      signal: ctrl.signal,
    })
      .then(async (res) => {
        const body = (await res.json().catch(() => ({}))) as {
          report?: OtchetSeo;
          quota?: OstatokSeo;
          error?: { code?: string };
        };
        if (ctrl.signal.aborted) return;
        if (res.ok && body.report)
          setSeo({ status: 'done', report: body.report, quota: body.quota ?? null });
        else if (
          body.error?.code === 'guest_quota_exceeded' ||
          body.error?.code === 'seo_payment_required'
        )
          setSeo({ status: 'limit' });
        else setSeo({ status: 'error' });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setSeo({ status: 'error' });
      });

    await Promise.all([aiJob, seoJob]);
  }, []);

  // Ссылка вида ?url=example.com открывает готовую проверку: ею делятся.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('url');
    if (q) {
      setUrl(q);
      void run(q);
    }
    return () => {
      live.current?.abort();
    };
  }, [run]);

  const aiScore = ai.status === 'done' ? ai.report.score : null;
  const seoScore = seo.status === 'done' ? seo.report.score : null;
  const overall =
    aiScore !== null && seoScore !== null
      ? Math.round((aiScore + seoScore) / 2)
      : (aiScore ?? seoScore);
  const busy = ai.status === 'loading' || seo.status === 'loading';

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run(url);
        }}
        className="flex flex-col gap-3 sm:flex-row"
      >
        <input
          type="text"
          inputMode="url"
          autoComplete="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
          }}
          placeholder={t('placeholder')}
          aria-label={t('input_label')}
          className="min-w-0 flex-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 py-3 text-base outline-none focus:border-[var(--color-accent-500)]"
        />
        <button
          type="submit"
          disabled={busy || url.trim() === ''}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--color-brand-500)] px-6 py-3 font-semibold text-[var(--color-on-brand)] transition-colors hover:bg-[var(--color-brand-400)] disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Search className="h-4 w-4" aria-hidden="true" />
          )}
          {t('check')}
        </button>
      </form>

      {started && (
        <div className="mt-8 space-y-8" aria-live="polite">
          {ai.status === 'loading' && (
            <p className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('checking')}
            </p>
          )}
          {ai.status === 'error' && (
            <p role="alert" className="text-sm text-red-500">
              {t(
                ai.code === 'bad_url'
                  ? 'err_bad_url'
                  : ai.code === 'rate_limited'
                    ? 'err_rate_limited'
                    : ai.code === 'unreachable'
                      ? 'err_unreachable'
                      : ai.code === 'network'
                        ? 'err_network'
                        : 'err_failed',
              )}
            </p>
          )}

          {ai.status === 'done' && (
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-6">
              <p className="mb-5 truncate font-semibold" title={ai.report.url}>
                {ai.report.url}
              </p>
              <div className="flex flex-wrap gap-8">
                <ScoreRing value={overall} label={t('overall')} />
                <ScoreRing value={aiScore} label={t('score_ai')} />
                <ScoreRing value={seoScore} label={t('score_seo')} />
              </div>
              <h2 className="mb-3 mt-6 text-lg font-semibold">{t('top_fixes')}</h2>
              <AdviceList
                items={ai.report.advice}
                seo={seo.status === 'done' ? glavnoeSeo(seo.report.issues) : []}
                ru={locale === 'ru'}
              />
              {ai.cached && (
                <p className="mt-3 text-xs text-[var(--color-text-muted)]">{t('cached')}</p>
              )}
            </div>
          )}

          {(ai.status === 'done' || seo.status !== 'idle') && (
            <div>
              <div role="tablist" className="mb-6 flex gap-2 border-b border-[var(--color-border)]">
                {(['ai', 'seo'] as const).map((k) => (
                  <button
                    key={k}
                    role="tab"
                    type="button"
                    aria-selected={tab === k}
                    onClick={() => {
                      setTab(k);
                    }}
                    className={`-mb-px border-b-2 px-4 py-2 font-medium ${
                      tab === k
                        ? 'border-[var(--color-accent-500)]'
                        : 'border-transparent text-[var(--color-text-muted)]'
                    }`}
                  >
                    {t(k === 'ai' ? 'tab_ai' : 'tab_seo')}
                  </button>
                ))}
              </div>
              <div role="tabpanel">
                {tab === 'ai' ? (
                  ai.status === 'done' ? (
                    <AiTab r={ai.report} />
                  ) : null
                ) : (
                  <SeoTab state={seo} />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
